const {
  formatMoney,
  getElement,
  getElements,
  translateString,
  viewportSize,
} = window.BAO.Utils

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  riseModal: '[data-product-rise-modal-button]',
  addToCartText: '[data-product-add-to-cart-text]',
  form: '[data-product-form]',
  formWrapper: '[data-product-form-wrapper]',
  notifyMe: '[data-product-notify-me]',
  price: '[data-product-price]',
  priceCompare: '[data-product-price-compare]',
  priceCompareText: '[data-product-price-compare-text]',
  sku: '[data-product-sku]',
  selectSize: '[data-product-select-size]',
  orderDeadline: '[data-product-order-deadline]',
  notificationTrigger: '[data-product-notification-trigger]',
  priceHTMLTemplate: '[data-product-price-html-template]',
  priceCompareHTMLTemplate: '[data-product-price-compare-html-template]',
}

class Variant {
  constructor (data) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data
  }

  setPrice () {
    unhidePriceEls()

    const priceFormat = `{{ amount_no_decimals }} ${window.Shopify.currency.active}`
    const priceElements = getElements(selectors.price)

    const price = formatMoney(this.data.price, priceFormat)
    const priceInnerHTML = translateString(
      getElement(selectors.priceHTMLTemplate).innerHTML,
      { price },
    )

    priceElements.forEach(el => {
      el.innerHTML = priceInnerHTML
    })

    const priceCompareElements = getElements(selectors.priceCompare)
    const compareAtPrice = formatMoney(this.data.compare_at_price, priceFormat)
    const compareAtPriceInnerHTML = translateString(
      getElement(selectors.priceCompareHTMLTemplate).innerHTML,
      { price: compareAtPrice },
    )
    priceCompareElements.forEach(el => {
      el.innerHTML = compareAtPriceInnerHTML
    })

    if (!this.data.compare_at_price) {
      hidePriceCompareElements()
    }
  }

  setAvailability () {
    const registeredCookie = Number(
      window.BAO.Utils.getCookie(`isRegistered-${this.data.id}`),
    )

    getElements(selectors.notifyMe).forEach(el => {
      const registeredMessageEl = el.nextElementSibling.nextElementSibling

      if (!this.data.available && registeredCookie === this.data.id) {
        registeredMessageEl.setAttribute('aria-hidden', 'false')
        el.setAttribute('aria-hidden', 'true')
      } else if (!this.data.available) {
        el.setAttribute('aria-hidden', 'false')

        const isMobile = viewportSize() === 'xs'

        /**
         * @warn It would be much better if registeredMessageEl didn't use `.nextSiblingElement.nextSiblingElement`,
         * but unfortunately I don't know enough about how the PDP here works to understand why nextSiblingElement
         * was used and don't have time to try to refactor this. - DW
         */
        if (!registeredMessageEl.matches('form') && isMobile) {
          registeredMessageEl.setAttribute('aria-hidden', 'true')
        }
      } else {
        el.setAttribute('aria-hidden', 'true')
        registeredMessageEl.setAttribute('aria-hidden', 'true')
      }
    })

    const showOrderDeadline = this.data.available

    getElements(selectors.formWrapper).forEach(el =>
      el.setAttribute('aria-hidden', !this.data.available),
    )
    getElements(selectors.orderDeadline).forEach(el =>
      el.setAttribute('aria-hidden', !showOrderDeadline),
    )
    getElements(selectors.notificationTrigger).forEach(el =>
      el.setAttribute('aria-hidden', showOrderDeadline),
    )
  }

  updateInput () {
    const inputs = getElements(`form[action*="/cart/add"] [name="id"]`)

    inputs.forEach(input => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    const shareButtons = getElements('share-button')
    if (shareButtons.length === 0) return

    shareButtons.forEach(shareButton => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const params = new URLSearchParams(window.location.search)
    params.set('variant', this.id.toString())

    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)
    url.search = params.toString()

    return url
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'option', 'option-choice']
  }

  constructor () {
    super()
    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new Variant(this.defaultVariant)]
  }

  setupListeners () {
    super.setupListeners()

    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(this, 'change', this.onVariantChange)
  }

  onVariantChange (e) {
    const variant = this.getVariant()
    getElements(selectors.addToCart).forEach(el => (el.disabled = false))
    variant.setAvailability()
    variant.setPrice()
    variant.updateInput()

    getElements(selectors.selectSize).forEach(el =>
      el.setAttribute('aria-hidden', 'true'),
    )

    getElements(selectors.riseModal).forEach(el => {
      el.classList.remove('btn-Button-inactive')
      el.classList.add('btn-Button-primary')
    })

    const variantChange = new CustomEvent('variantChange', {
      detail: {
        variant,
        hasVariant: !!this.currentVariant,
      },
    })

    document.dispatchEvent(variantChange)

    if (!this.currentVariant) {
      this.resetPage(variant)
      getElements(selectors.formWrapper).forEach(el =>
        el.setAttribute('aria-hidden', 'true'),
      )
      getElements('pdp-back-in-stock-flow').forEach(el => {
        el.hideEmailForm()
      })
      getElements(selectors.notifyMe).forEach(el => {
        el.setAttribute('aria-hidden', 'true')
        el.nextElementSibling.nextElementSibling.setAttribute(
          'aria-hidden',
          'true',
        )
      })
      getElements(selectors.selectSize).forEach(el =>
        el.setAttribute('aria-hidden', 'false'),
      )
      getElements(selectors.riseModal).forEach(el => {
        el.classList.add('btn-Button-inactive')
        el.classList.remove('btn-Button-primary')
      })
      return
    }

    variant.setUrl()
    variant.setShareUrl()
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage (variant) {
    window.history.replaceState(
      { path: window.location.pathname },
      '',
      window.location.pathname,
    )
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new Variant(data)
    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    if (!this.currentVariant) {
      return this.collectedVariants[0]
    }

    const existingVariant = this.collectedVariants.find(
      variant => variant.id === this?.currentVariant?.id,
    )
    if (existingVariant) return existingVariant

    return this.createVariant(this.currentVariant)
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions () {
    this.throwError(`selectedOptions needs to be implemented on this class`)

    return []
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this.data.find(variant => {
      return variant.options.every(
        (option, index) => this.selectedOptions[index].value === option,
      )
    })
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    return JSON.parse(this.els.data.element.innerText)
  }
}

export class VariantRadios extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset
      const choice = getElements(this.els['option-choice'].selector, {
        context: el,
      }).find(el => el.checked).value

      return {
        label: name,
        value: choice,
      }
    })
  }
}

export class VariantSelects extends VariantOptions {
  constructor () {
    super()
    this.syncVariantChanges()
  }

  isVisible () {
    const style = getComputedStyle(this)
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.opacity === '0'
    ) {
      return false
    }

    if (this.offsetParent === null && style.position !== 'fixed') {
      return false
    }

    return !this.closest('[hidden]')
  }

  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset
      return {
        label: name,
        value: el.querySelector('select').value,
      }
    })
  }

  syncVariantChanges () {
    if (!this.els.variant?.exists) return

    const params = new URLSearchParams(window.location.search)
    const urlVariant = params.get('variant')

    if (urlVariant) {
      const variantData = this.data.find(
        item => item.id.toString() === urlVariant.toString(),
      )
      if (variantData) {
        const { title } = variantData

        if (title) {
          this.els.variant.elements.forEach(el => {
            if (el.value === title) {
              el.selected = true
              if (this.isVisible()) {
                this.onVariantChange()
              }
            }
          })
        }
      }
    }
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios)
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects)
}

function hideElements (elements) {
  elements.forEach(el => (el.hidden = true))
}

function unhideElements (elements) {
  elements.forEach(el => (el.hidden = false))
}

function unhidePriceEls () {
  unhideElements(getElements(`${selectors.price}, ${selectors.priceCompare}`))
}

function hidePriceCompareElements () {
  hideElements(getElements(selectors.priceCompare))
}
